import { Subscription, BehaviorSubject, Subject } from "rxjs";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  ApplicationRef,
  ElementRef,
  Injector,
  NgZone,
  AfterViewInit,
  OnDestroy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
} from "@angular/core";

import { GoldenLayoutService } from "../golden-layout/golden-layout-service";
import { TranslateService } from "@ngx-translate/core";
import {
  DetailFillTableDefinitionFactory,
  TableDefinition,
  ColumnDefinition,
  SystemConfigPromiseService,
  PositionBookPromiseLookupService,
  CurrencyPairCalendarPrimaryLookupService,
  COLUMN_VALUE_DATE,
} from "shared/cubiq-services";
// import { BfxVwapOrdersService } from "../bfx-vwap/bfx-vwap-orders.service";
import {
  GridOptions,
  RowNode,
  StartEditingCellParams,
  TabToNextCellParams,
  RowSelectedEvent,
  CellValueChangedEvent,
  CellClassParams,
  CellPosition,
  ICellEditorParams,
} from "ag-grid-community/main";
import { BfxBaseBlotterColumnSelectorService } from "../bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { BfxMouseModifierKeyService } from "app/bfx-common/services/bfx-mouse-modifier-key.service";
import { FillOrderGroup } from "./models/FillOrderGroup";
import { DfAmountSpinner } from "./editors/DfAmountSpinner";
import { DfCheckBox } from "./editors/DfCheckBox";
import { MarketSpotSpinner } from "./editors/MarketSpotSpinner";
import { MarketPtsSpinner } from "./editors/MarketPtsSpinner";
import { MarketAllInSpinner } from "./editors/MarketAllInSpinner";
import { ClientSpotSpinner } from "./editors/ClientSpotSpinner";
import { ClientPtsSpinner } from "./editors/ClientPtsSpinner";
import { ClientAllInSpinner } from "./editors/ClientAllInSpinner";
import { PosBookAuto } from "./editors/PosBookAuto";
import { VehicleCcyAuto } from "./editors/VehicleCcyAuto";

const DEFAULT_CELL_CLASS: string = "ag-cell-default";

export const DEFAULT_ROW_HEIGHT: number = 24;

export function provideBlotter(component: any) {
  return {
    provide: BfxDetailFillGridComponent,
    useExisting: forwardRef(() => component),
  };
}
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import { GammaUtils } from "app/bfx-blotter-orders/GammaUtils";
import {
  ROW_SELECTION_FIRST_CLASS,
  ROW_SELECTION_LAST_CLASS,
  ROW_SELECTION_MARKER,
} from "app/bfx-base-blotter/bfx-base-blotter";
import { AgGridEditableBodyScroll } from "../shared/ag-grid/AgGridEditableBodyScroll";
import { DfCalendarComponent } from "./editors/DfCalendar";
import { BfxDetailFillService } from "./services/bfx-detail-fill.service";
import { DetailFillOrderData } from "../../shared/cubiq-services/mediator/adapter/DetailFillOrderData";
import { CommonIntentRenderer } from "app/bfx-blotter-orders/renderers/CommonIntentRenderer";

@Component({
  selector: "bfx-detail-fill-grid",
  // old styling (not refactored)
  templateUrl: "./html/bfx-detail-fill-grid.html",
  standalone: false,
})
export class BfxDetailFillGridComponent implements OnDestroy, AfterViewInit {
  public static instanceId = 0;

  public blotterId: string;
  public rowHeight = DEFAULT_ROW_HEIGHT;
  public height: number = 500;

  @Output("selectedOrdersChange")
  public selectedOrdersEmitter: EventEmitter<any> = new EventEmitter();

  @Output("gridLayoutChange")
  public gridLayoutChangeEmitter: EventEmitter<any> = new EventEmitter();

  private ensureColumnVisibleTimeoutId: NodeJS.Timer = null;

  @Input("fillOrderGroup")
  public _fillOrderGroup: FillOrderGroup;

  protected primed: boolean = false;
  protected tableData: any = null;
  public restored: boolean = false;
  @Input("columnsConfig")
  public columnsConfiguration: any = {};

  private sortingModel: any = [{ colId: "selection", sort: "asc" }];
  public defaultSortingModel: any = null;
  public blotterInstance: any = null;

  public tableDefinition: TableDefinition = null;
  public columnDefs: Array<any> = [];
  public frameworkComponents: any = {
    marketSptSpinner: MarketSpotSpinner,
    marketPtsSpinner: MarketPtsSpinner,
    marketAllInSpinner: MarketAllInSpinner,
    clientSptSpinner: ClientSpotSpinner,
    clientPtsSpinner: ClientPtsSpinner,
    clientAllInSpinner: ClientAllInSpinner,
    stpCheckBox: DfCheckBox,
    posBookAutoComplete: PosBookAuto,
    fillAmtSpinner: DfAmountSpinner,
    vehicleAuto: VehicleCcyAuto,
    intentRenderer: CommonIntentRenderer,
    valueDateCalendar: DfCalendarComponent,
  };

  public gridOptions: GridOptions;

  public blotterView: ElementRef;
  private dataAddedSubscription: Subscription;
  private dataRemovedSubscription: Subscription;
  private dataChangedSubscription: Subscription;
  private pendingChangedSubscription: Subscription;
  private eventSubscription: Subscription;
  private vwapChangeSubscription: Subscription;
  private gridBusy: boolean = false;
  public viewInitComplete: boolean = false;

  private columnsThatAffectVwap: string[] = [
    "fillAmount",
    "spotMarketRate",
    "forwardMarketPoints",
    "forwardMarketRate",
    "clientSpot",
    "forwardPoints",
    "allInClientRate",
  ];

  fillResultErrorSubscription: Subscription;
  private tdrNotificationSubscription: Subscription;

  constructor(
    BfxBlotterElement: ElementRef,
    applicationRef: ApplicationRef,
    injector: Injector,
    public zone: NgZone,
    private _tableDefinitionFactory: DetailFillTableDefinitionFactory,
    protected layoutService: GoldenLayoutService,
    protected translationService: TranslateService,
    protected columnSelectorService: BfxBaseBlotterColumnSelectorService,
    protected sysConfigService: SystemConfigPromiseService,
    protected mouseModifierKeyService: BfxMouseModifierKeyService,
    private posBookService: PositionBookPromiseLookupService,
    public detailFillService: BfxDetailFillService,
    public calendarService: CurrencyPairCalendarPrimaryLookupService,
  ) {
    (this.constructor as any).instanceId++;
    this.blotterId = this.constructor.name; // + "_" + (this.constructor as any).instanceId;
    this.blotterInstance = this;
    this.blotterView = BfxBlotterElement;

    let funcGetRowNodId = function (data) {
      if (data && this.tableDefinition != null)
        return String(data[this.tableDefinition.primaryKey]);
      return "";
    }.bind(this);

    this.gridOptions = <GridOptions>{
      getRowNodeId: funcGetRowNodId,
      rowSelection: "multiple",
      deltaRowDataMode: true,
      alignedGrids: [],
      headerHeight: 24,
      suppressContextMenu: true,
      stopEditingWhenGridLosesFocus: true,
      suppressColumnMoveAnimation: true,
      suppressDragLeaveHidesColumns: true,
      suppressRowClickSelection: true,
      suppressScrollOnNewData: true,
      suppressTabbing: false,
      suppressFocusAfterRefresh: true,
      suppressAutoSize: true,
      showToolPanel: false,
      toolPanelSuppressSideButtons: true,
      enableFilter: false,
      treeData: true,
      rowDeselection: false,
      enableBrowserTooltips: true,
      getRowHeight: function (params) {
        return DEFAULT_ROW_HEIGHT;
      },

      onGridReady: (params) => {
        // rest of initialization happens after ag-grid is ready
        if (this.gridOptions != null) {
          this.gridOptions.api = params.api;
          this.gridOptions.columnApi = params.columnApi;

          if (this.gridOptions.api != null) {
            this.gridOptions.api.showLoadingOverlay();
          }
        }

        this._tableDefinitionFactory.defineTable().then(
          (value) => {
            this.tableDefinition = value;
            this.initializeColumnDefinitions();
            this.gridOptions.api.setRowData(this._fillOrderGroup.rowData);

            this.gridOptions.api.selectAll();
            this.dataAddedSubscription = this._fillOrderGroup.addedSubject.subscribe(
              this.onAddedEvent.bind(this),
            );
            this.dataRemovedSubscription = this._fillOrderGroup.removedSubject.subscribe(
              this.onRemovedEvent.bind(this),
            );
            this.dataChangedSubscription = this._fillOrderGroup.dataChangedSubject.subscribe(
              this.onUpdateEvent.bind(this),
            );
            this.pendingChangedSubscription = this._fillOrderGroup.pendingChangeSubject.subscribe(
              ({ deselectIds, selectIds }) => {
                this.onPendingUpdateEvent(deselectIds, selectIds);
              },
            );
            this.vwapChangeSubscription = this._fillOrderGroup.vwapUpdateSubject.subscribe(() => {
              this.handleVWapColumnsChanged(null);
            });
          },
          (reason: string) => {
            logger.out.info(
              "RawDataOrderAdapter:_tableDefinitionFactory.defineTabl rejection: " + reason,
            );
          },
        );
      },

      tabToNextCell: (params: TabToNextCellParams) => {
        let nextCellPosition: CellPosition = params.nextCellPosition;

        if (
          nextCellPosition &&
          nextCellPosition.column &&
          nextCellPosition.column.getColDef().editable
        ) {
          let col = this.gridOptions.columnApi.getDisplayedColAfter(nextCellPosition.column);
          if (col) {
            this.gridOptions.api.ensureColumnVisible(col);
          } else {
            this.gridOptions.api.ensureColumnVisible(nextCellPosition.column);
          }

          let selectedRow: RowNode = this.gridOptions.api.getDisplayedRowAtIndex(
            nextCellPosition.rowIndex,
          );
          let editable: boolean = nextCellPosition.column.isCellEditable(selectedRow);

          if (editable) {
            setTimeout(() => {
              let params: StartEditingCellParams = {
                rowIndex: nextCellPosition.rowIndex,
                colKey: nextCellPosition.column,
              };
              this.gridOptions.api.startEditingCell(params);
            }, 50);
          }
        }
        if (nextCellPosition) {
          return {
            rowIndex: nextCellPosition.rowIndex,
            column: nextCellPosition.column,
            rowPinned: nextCellPosition.rowPinned,
          };
        }
        return null;
      },

      onColumnMoved: () => {
        // need to update the grid layout
        this.gridLayoutChangeEmitter.emit();
      },
      onColumnVisible: () => {
        // need to update the grid layout
        this.gridLayoutChangeEmitter.emit();
      },
      onColumnResized: () => {
        // need to update the grid layout
        this.gridLayoutChangeEmitter.emit();
      },
      onSortChanged: () => {
        // need to update the grid layout
        this.gridLayoutChangeEmitter.emit();
      },
      getDataPath: (data) => {
        return data.key.split("-");
      },
      context: {
        isCellEditingEnabled: (params: ICellEditorParams) => {
          // dfCalendar
          if (params.colDef.field !== COLUMN_VALUE_DATE) return true;

          let order = params.data as DetailFillOrderData;

          return (
            (order.orderType === CubiqTS.OrderType.LIMIT_SL ||
              order.orderType === CubiqTS.OrderType.LIMIT_TP ||
              order.orderType === CubiqTS.OrderType.LIMIT_CALL) &&
            order.strategyType !== CubiqTS.StrategyType.GAMMA
          );
        },
        minDate: new BehaviorSubject(new Date()),
        editingStoppedSubject: new Subject(),
      },
      groupDefaultExpanded: -1,
      groupSuppressAutoColumn: true,
      isRowSelectable: (row) => {
        if (!row.data || row.data.isComponentLeg) {
          return false;
        }
        return true;
      },
      onRowSelected: (event: RowSelectedEvent) => {
        if (event.type == "rowSelected") {
          if (event.node.isSelected()) {
            if (event.data.leg1 && event.data.leg2) {
              event.data.leg1.legEnabled = true;
              event.data.leg2.legEnabled = true;
              this.gridOptions.api.batchUpdateRowData({
                update: [event.data.leg1, event.data.leg2],
              });
            }

            if (event.data.gammaRungId != null) {
              this.doGammaRungSelect(event.data);
            }
          } else {
            if (event.data.leg1 && event.data.leg2) {
              event.data.leg1.legEnabled = false;
              event.data.leg2.legEnabled = false;
              this.gridOptions.api.batchUpdateRowData({
                update: [event.data.leg1, event.data.leg2],
              });
            }
            if (event.data.gammaRungId != null) {
              this.doGammaRungUnSelect(event.data);
            }
          }
        }
      },
      rowClassRules: {
        "leg-enabled": function (params) {
          return params.data.legEnabled;
        },
        "part-fill-disabled": function (params) {
          return params.data.isComponentLeg || params.data.partFillDisabled;
        },
        "stp-disabled": function (params) {
          return params.data.isComponentLeg || params.data.stpDisabled;
        },
        "vehicle-disabled": function (params) {
          return params.data.isComponentLeg;
        },
        "clientSpot-disabled": function (params) {
          return params.data.isComponentLeg;
        },
        /*
				"pos-book-disabled" : function(params) { return params.data.posBookDisabled},
*/
        "fwdEdit-disabled": function (params) {
          return (
            params.data.product !== CubiqTS.Product.FWD &&
            params.data.product !== CubiqTS.Product.NDF
          );
        },
      },
    };

    this.fillResultErrorSubscription = detailFillService.fillResultError$.subscribe(
      (result: CubiqTS.OrderFillResult) => {
        let node = this.getRowNode(result);
        if (
          result.errors.errorMessages.some((errorMsg) => {
            let errorObject: any = JSON.parse(errorMsg + "");
            return (
              errorObject.id == "value.dt.in.past" ||
              errorObject.id == "str.value.date.falls.on.holiday"
            );
          })
        ) {
          node.data.valueDateInvalid = true;
        }
      },
    );

    this.tdrNotificationSubscription = this.calendarService.getTdrNotification().subscribe(() => {
      if (this.gridOptions?.api != null) {
        this.gridOptions.api.forEachNode((rowNode) => {
          let order: DetailFillOrderData = rowNode.data;
          let lockValueDate = this._tableDefinitionFactory.myValueDateHelper.getMoment(
            order as any,
          );

          // consider for any calendar change, not just TDR
          if (
            !order.lockedValueDate.isSame(lockValueDate) &&
            order.valueDateRequested &&
            order.valueDate != null &&
            lockValueDate.isSame(order.valueDate)
          ) {
            order.valueDate = null;
            order.valueDateRequested = false;
          }
        });
        this.gridOptions.api.refreshCells({ force: true });
      }
    });

    NativeDesktopSupport.prepareGridOptions(this.gridOptions, BfxBlotterElement.nativeElement);
  }

  doGammaRungSelect(selected: any) {
    let nextRungIn = selected.nextGammaRungClosestToMarket;
    if (null != nextRungIn) {
      let node = this.getRowNode(nextRungIn);
      let row = node.data;

      // if we have an oldValue (part fill) - we need to revert it
      // ...on the next rung in...
      if (CubiqTS.StrategyType.GAMMA == row.strategyType && null != row.retroFillAmount) {
        row.fillAmount = row.retroFillAmount;
        row.retroFillAmount = null;
        this.gridOptions.api.batchUpdateRowData({
          update: [row],
        });
      }

      // and select it
      this.selectOrder([nextRungIn]);
    }
  }

  doGammaRungUnSelect(data: any) {
    let outside: CubiqTS.OrderData[] = GammaUtils.getRungsOutside(data);
    if (null != outside && outside.length > 0) {
      this.selectOrder([outside[0]], false);
    }
  }

  /**
   * Given an Array of Orders - select them in the model
   * This should cause a rowSelected Event in case it needs to be chained.
   *
   * @param orders
   */
  selectOrder(orders: Array<CubiqTS.OrderData>, selected: boolean = true) {
    for (let order of orders) {
      if (null != order && null != order.orderId) {
        let ret = this.getRowNode(order);
        if (null != ret && undefined != ret) {
          ret.setSelected(selected);
        }
      }
    }
  }

  getRowNode(order: { orderId: CubiqTS.Long | string }): RowNode {
    let ret: RowNode = null;
    if (null != order && null != order.orderId) {
      ret = this.gridOptions.api.getRowNode(order.orderId.toString());
    }
    return ret;
  }

  ngAfterViewInit() {
    this.viewInitComplete = true;
  }

  public clearGridCellSelection(): void {
    if (this.gridOptions != null && this.gridOptions.api != null) {
      this.gridOptions.api.clearFocusedCell();
    }
  }

  protected initializeColumnDefinitions(): void {
    this.gridOptions.onSelectionChanged = () => {
      if (this.gridOptions.api) {
        let col = this.gridOptions.columnApi.getColumn("selection");
        let colDef = col.getColDef();
        colDef.headerName = "(" + this.selectedRowsLength() + ")";
        colDef.headerTooltip = colDef.headerName;
        this.gridOptions.api.refreshHeader();
        this._fillOrderGroup.selectionChanged(this.gridOptions.api.getSelectedRows());
        this.selectedOrdersEmitter.emit();
      }
      this.onSelectionChanged();
    };

    this.gridOptions.onCellClicked = (event) => {
      if (this.gridOptions.api) {
        if (event.colDef.editable) {
          this.gridOptions.api.ensureColumnVisible(event.column);
          let unfocus: boolean = false;
          switch (event.colDef.field) {
            case "fillAmount":
              unfocus = !event.data.partFillEnabled;
              break;
            case "stp":
              unfocus = !event.data.canOverRideStp;
              break;
            case "positionBookName":
              unfocus = event.data.requestedOrderType == CubiqTS.RequestOrderType.CALL;
              break;
          }
          if (unfocus) {
            this.clearGridCellSelection();
          }
        }
      }

      this.gridOptions.onBodyScroll = AgGridEditableBodyScroll(this);
    };

    this.gridOptions.onCellValueChanged = (event) => {
      switch (event.colDef.field) {
        case "fillAmount": {
          this.handleFillAmountChanged(event);
          break;
        }
        case "vehicleCcy": {
          this.handleVehicleCCYChanged(event);
          break;
        }
      }

      if (this.columnsThatAffectVwap.includes(event.colDef.field)) {
        this.handleVWapColumnsChanged(event);
      }
    };

    this.applyColumnDefinitions();
  }

  handleFillAmountChanged(event: CellValueChangedEvent) {
    let bnNewVal: CubiqTS.BigNumber = event.newValue;
    let bnOldVal: CubiqTS.BigNumber = event.oldValue;

    if (!bnNewVal.isEqualTo(bnOldVal)) {
      if (event.data.gammaRungId != null) {
        // we need to stash the previous Fill amount on the row - in case of a rollback
        event.data.retroFillAmount = bnOldVal;
        // and unselect the next Row outside (which will ripple out)
        this.doGammaRungUnSelect(event.data);
      }
    }
  }

  handleVWapColumnsChanged(event: CellValueChangedEvent) {
    this._fillOrderGroup.doVwap();
    this.selectedOrdersEmitter.emit();
  }

  handleVehicleCCYChanged(event: CellValueChangedEvent) {
    if (event.newValue != event.oldValue) {
      if (event.newValue == "-") {
        this._fillOrderGroup.vehicleCcyChange(event.data.orderId, null);
      } else {
        let pairArr: Array<string> = event.data.instrument.split("/");
        let base: string = pairArr[0];
        let nonBase: string = pairArr[1];
        let tradingGroupId: number = Number(event.data.deskId);
        this.posBookService
          .getPositionBooksForPairAndTradingGroup(base, nonBase, tradingGroupId, event.newValue)
          .then((values) => {
            this._fillOrderGroup.vehicleCcyChange(
              event.data.orderId,
              event.newValue,
              values.leg1,
              values.leg2,
            );
          });
      }
    }
  }

  /**
   * Runs after selection changes and marks the first and last rows
   * of a selection band. Used by cell class rules to render along with row selection
   * class
   */
  public onSelectionChanged(): void {
    let selected = this.gridOptions.api?.getSelectedNodes();

    if (selected && selected.length > 0) {
      let lastRowIndex: number = null;
      let lastIndex: number = null;

      selected
        .sort((a, b) => a.rowIndex - b.rowIndex)
        .forEach((r, index) => {
          if (lastRowIndex == null || lastRowIndex < r.rowIndex - 1) {
            if (lastRowIndex != null) {
              selected[lastIndex][ROW_SELECTION_MARKER].push(ROW_SELECTION_LAST_CLASS);
            }
            r[ROW_SELECTION_MARKER] = [ROW_SELECTION_FIRST_CLASS];
          } else {
            r[ROW_SELECTION_MARKER] = [];
          }

          lastRowIndex = r.rowIndex;
          lastIndex = index;
        });

      if (
        lastRowIndex != null &&
        selected[lastIndex] &&
        selected[lastIndex][ROW_SELECTION_MARKER]
      ) {
        selected[lastIndex][ROW_SELECTION_MARKER].push(ROW_SELECTION_LAST_CLASS);
      }

      this.gridOptions.api.refreshCells({ rowNodes: selected, force: true });
    }
  }

  /**
   * Returns the row items currently selected aside from the ones
   * that have IDs filtered out by the isValidRowID method
   */
  protected getSelectedRows(): Array<any> {
    let selectedItems = this.gridOptions.api.getSelectedRows();

    if (selectedItems != null && selectedItems.length > 0) {
      selectedItems = selectedItems.filter(
        (row: any) =>
          row[this.tableDefinition.primaryKey] != null &&
          this.isValidRowID(row[this.tableDefinition.primaryKey]),
      );
    }

    return selectedItems;
  }

  /**
   * Indicates if the parameter sent is a valid row ID for this blotter. Invalid
   * ones are filtered out from the selection segment provided by the getSelectedRows method
   * @param rowId the rows ID to be checked
   */
  protected isValidRowID(rowId: string): boolean {
    return true;
  }

  protected canRowSelect(row): boolean {
    let select = !this._fillOrderGroup.queuedIds.has(row.data.orderId);
    if (!select) {
      row.node.setSelected(false);
    }
    return select;
  }

  protected applyColumnDefinitions(): void {
    let defs: Array<any> = this.tableDefinition.columns;
    defs.forEach((col: any) => {
      if (this.columnsConfiguration != null && this.columnsConfiguration[col.field]) {
        let config = this.columnsConfiguration[col.field];

        if (config.width) {
          col.width = config.width;
        }

        if (config.position) {
          col.position = config.position;
        }

        if (config.visible != null) {
          col.hide = !config.visible;
        }
      }

      col.suppressKeyboardEvent = function (event) {
        if (event && event.event && (event.event.code == "Tab" || event.event.code == "Enter")) {
          return false;
        }
        return event.editing;
      };
      col.suppressNavigable = function (params) {
        if (!params.colDef.editable) {
          return true;
        }
        switch (params.colDef.field) {
          case "fillAmount":
            return params.data.isComponentLeg || !params.data.partFillEnabled;
          case "stp":
            return params.data.isComponentLeg || !params.data.canOverRideStp;
          case "positionBookName":
            return (
              params.data.requestedOrderType == CubiqTS.RequestOrderType.CALL ||
              (params.data.leg1 != undefined && params.data.leg2 != undefined)
            );
          case "clientSpot":
            return params.data.isComponentLeg;
          case "vehicleCcy":
            return params.data.isComponentLeg;
        }
        return false;
      };

      if (col.cellClass == undefined || col.cellClass == null || col.cellClass.length == 0) {
        col.cellClass = [DEFAULT_CELL_CLASS];
      }

      if (col.cellClassRules == null) {
        col.cellClassRules = {};
      }

      // First and last row of selection cell classes
      col.cellClassRules[ROW_SELECTION_FIRST_CLASS] = (params: CellClassParams) => {
        if (params.node != null && params.node[ROW_SELECTION_MARKER] != null) {
          return params.node[ROW_SELECTION_MARKER].includes(ROW_SELECTION_FIRST_CLASS);
        }
        return false;
      };

      col.cellClassRules[ROW_SELECTION_LAST_CLASS] = (params: CellClassParams) => {
        if (params.node != null && params.node[ROW_SELECTION_MARKER] != null) {
          return params.node[ROW_SELECTION_MARKER].includes(ROW_SELECTION_LAST_CLASS);
        }
        return false;
      };
    });

    //defs.splice(defs.length - 1, 1);
    defs.sort((a, b) => {
      return a.position - b.position;
    });

    this.columnDefs = defs;
  }

  protected applyComparator(col: any, column: ColumnDefinition): void {
    // let columnName: string = 'as_sort_' + column.data;
    // switch (column.indexType) {
    // 	case IndexType.Boolean:
    // 	case IndexType.String:
    // 		col.comparator = this.sortString.bind(this, columnName);
    // 		break;
    // 	case IndexType.Double:
    // 	case IndexType.Integer:
    // 		col.comparator = this.sortNumber.bind(this, columnName);
    // 		break;
    // 	case IndexType.Long:
    // 		col.comparator = this.sortLong.bind(this, columnName);
    // 		break;
    // 	case IndexType.NaturalString:
    // 		col.comparator = this.sortBigNumber.bind(this, columnName);
    // 		break;
    // }
  }

  protected sortString(columnName: string, valueA: string, valueB: string, nodeA, nodeB): number {
    let strA: string = nodeA.data[columnName];
    if (!strA) strA = "";
    let strB: string = nodeB.data[columnName];
    if (!strB) strB = "";
    return strA.localeCompare(strB);
  }

  protected sortNumber(columnName: string, valueA: number, valueB: number, nodeA, nodeB): number {
    return nodeA.data[columnName] - nodeB.data[columnName];
  }

  protected sortLong(
    columnName: string,
    valueA: CubiqTS.Long,
    valueB: CubiqTS.Long,
    nodeA,
    nodeB,
  ): number {
    return nodeA.data[columnName].compare(nodeB.data[columnName]);
  }

  protected sortBigNumber(
    columnName: string,
    valueA: string,
    valueB: string,
    nodeA,
    nodeB,
  ): number {
    return this.stringToBigNum(nodeA.data[columnName]).comparedTo(
      this.stringToBigNum(nodeB.data[columnName]),
    );
  }
  private stringToBigNum(val: string): CubiqTS.BigNumber {
    if (!val || val == "" || val == "-") {
      return new CubiqTS.BigNumber(0);
    }
    return new CubiqTS.BigNumber(val);
  }

  protected renderCell(params): string {
    return params.value != null ? params.value : "";
  }

  protected editorCell(params): string {
    return params.value != null ? params.value : "";
  }

  protected renderEmpty(params): string {
    return "";
  }

  protected initializeInMemory(): void {
    // No op
  }

  public clearTable(): void {
    if (this.tableDefinition != null && this.gridOptions != null && this.gridOptions.api != null) {
      this.gridOptions.api.setRowData([]);
    } else {
      logger.out.warn(`${this.constructor.name}.clearTable() failed`);
    }
  }

  public setTableLoadPending(): void {
    this.tableData = null;
    this.clearTable();
    if (this.gridOptions && this.gridOptions.api) this.gridOptions.api.showLoadingOverlay();
  }

  protected blotterReady(): void {
    // No op
  }

  protected getItemId(row: any): string {
    return row != null ? row[this.tableDefinition.primaryKey] : null;
  }

  // overide to perform any actions after data for the blotter is primed
  protected onAdaptedDataPrimed(): void {
    this.primed = true;
  }

  ngOnDestroy() {
    if (this.fillResultErrorSubscription != null) {
      try {
        this.fillResultErrorSubscription.unsubscribe();
      } catch (err) {
        logger.out.warn(
          `${this.constructor.name}: Error closing fill result error subscription.`,
          err,
        );
      }
    }

    if (this.tdrNotificationSubscription != null) {
      try {
        this.tdrNotificationSubscription.unsubscribe();
      } catch (err) {
        logger.out.warn(
          `${this.constructor.name}: Error closing TDR notification subscription.`,
          err,
        );
      }
    }
  }

  protected addListenersToTable(): void {
    // No op
  }

  resize() {
    // this.height = this.topMenuView.nativeElement.parentElement.clientHeight - 40;
  }

  public resizeScrollBarForData() {
    // No op
  }

  public getComponentConfiguration(): any {
    return {
      blotterId: this.blotterId,
      columnsConfiguration: this.getBlotterColumnsConfigs(),
      sortingModel: this.getBlotterSortingModel(),
    };
  }

  public restoreFromConfiguration(config: any): void {
    this.columnsConfiguration = config.columnsConfiguration;
    this.sortingModel = config.sortingModel;
    this.restored = true;
    if (this.tableDefinition) {
      this.applyColumnDefinitions();
    }
  }

  public getBlotterId(): string {
    return this.blotterId;
  }

  protected checkForNoRows(): void {
    if (
      this.primed &&
      this.gridOptions.api.getModel()["rootNode"].childrenAfterFilter.length == 0
    ) {
      this.gridOptions.api.showNoRowsOverlay();
    } else if (this.primed) {
      // guard needed to prevent the
      // hiding of the loading overlay
      // which is shared with no-rows
      this.gridOptions.api.hideOverlay();
    } /* else {
			// implict from pre-prime initialization:
			this.gridOptions.api.showLoadingOverlay();
		} */
  }

  // handle the click of the ag-grid column header
  public handleClick(event) {
    this.columnSelectorService.doColumnSelectorMenu(this, this.gridOptions, event);
  }

  public getBlotterColumnsConfigs(): any {
    if (
      this.gridOptions == null ||
      this.gridOptions.columnApi == null ||
      typeof this.gridOptions.columnApi.getAllGridColumns !== "function"
    ) {
      logger.out.error(
        `${this.constructor.name}.saveAsDefault() failed ag-grid gridOptions are invalid`,
      );
      // column config won't be restored but defaults will still apply
      return null;
    }
    let columnsToSave = {};
    let positionCounter = 0;
    this.gridOptions.columnApi.getAllGridColumns().forEach((gridCol) => {
      let column = {
        id: "",
        width: 150,
        position: 1,
        visible: true,
      };
      column.id = gridCol.getColId();
      column.width = gridCol.getActualWidth();
      column.visible = gridCol.isVisible();
      column.position = positionCounter;

      columnsToSave[gridCol.getColId()] = column;
      positionCounter++;
    });
    this.columnsConfiguration = columnsToSave;
    return columnsToSave;
  }

  //this can be moved to base once the ag grid has being implemented
  public getBlotterSortingModel(): Array<any> {
    // this.sortingModel = this.gridOptions.api.getSortModel();
    if (
      this.gridOptions == null ||
      this.gridOptions.api == null ||
      typeof this.gridOptions.api.getSortModel !== "function"
    ) {
      logger.out.error(
        `${this.constructor.name}.saveAsDefault() failed ag-grid gridOptions are invalid`,
      );
      // sort model won't be restored but defaults will still apply
      return null;
    }

    return this.sortingModel;
  }

  protected isAllSelected(): boolean {
    return (
      this.gridOptions.api.getModel()["rootNode"].childrenAfterFilter.length ===
      this.gridOptions.api.getSelectedRows().length
    );
  }

  //this can be moved to base once the ag grid has being implemented
  public restoreSortFromConfig(): void {
    // if (this.gridOptions.api && this.sortingModel) {
    // 	this.gridOptions.api.setSortModel(this.sortingModel);
    // }
  }

  protected onUpdateEvent(updatedRows: Array<any>): void {
    if (this.gridOptions && this.gridOptions.api) {
      this.gridOptions.api.batchUpdateRowData({ update: updatedRows });
    }
  }

  protected onPendingUpdateEvent(deselectIds: string[], selectIds: string[]): void {
    if (this.gridOptions && this.gridOptions.api) {
      if (Array.isArray(deselectIds)) {
        for (let deselectId of deselectIds) {
          this.gridOptions.api.getRowNode(deselectId).setSelected(false);
        }
      }

      if (Array.isArray(selectIds)) {
        for (let reselectId of selectIds) {
          this.gridOptions.api.getRowNode(reselectId).setSelected(true, false);
        }
      }
    }
  }

  protected onAddedEvent(row: any): void {
    if (this.gridOptions && this.gridOptions.api) {
      this.addComplete(row);
    }
  }

  protected addComplete(row: any): void {
    // FIXME: need to introduce a rxjs auditTime subject to reduce number of setRowData calls

    // as this table is not sorted - aggrid will not see the resorted data - so we need to update it.
    // see FillOrdergroup::sortFillOrders for the sorting
    if (this._fillOrderGroup.ids.length > 0) {
      setTimeout(this.refreshDataInGrid.bind(this, row), 0);
    }

    // let existingRow = this.gridOptions.api.getRowNode(row.key);
    // if(existingRow){
    // 	this.gridOptions.api.getRowNode(row.key).setSelected(true);
    // }
  }

  protected onRemovedEvent(row: any): void {
    if (!this.gridBusy) {
      this.gridBusy = true;
      if (this._fillOrderGroup.ids.length > 0) {
        setTimeout(this.refreshDataInGrid.bind(this), 0);
      }
    }
    if (this._fillOrderGroup.ids.length == 0) {
      if (this.dataChangedSubscription) {
        this.dataChangedSubscription.unsubscribe();
      }
      if (this.pendingChangedSubscription) {
        this.pendingChangedSubscription.unsubscribe();
      }
      if (this.dataAddedSubscription) {
        this.dataAddedSubscription.unsubscribe();
      }
      if (this.dataRemovedSubscription) {
        this.dataRemovedSubscription.unsubscribe();
      }
      if (this.eventSubscription) {
        this.eventSubscription.unsubscribe();
      }
      if (this.vwapChangeSubscription) {
        this.vwapChangeSubscription.unsubscribe();
      }
    }
  }

  protected selectedRowsLength(): number {
    return this.gridOptions.api.getSelectedRows().length;
  }

  protected refreshDataInGrid(row = null): void {
    if (this.tableDefinition != null && this.gridOptions && this.gridOptions.api) {
      this.gridOptions.api.setRowData(this._fillOrderGroup.rowData);
      if (row != null)
        this.gridOptions.api.forEachNodeAfterFilter(function (node) {
          // select the specific new node
          if (node != null && node.data != null && row.orderId === node.data.orderId) {
            node.setSelected(true);
          }
        });
    } else {
      logger.out.warn(`${this.constructor.name}.refreshDataInGrid() failed`);
    }
    this.gridBusy = false;
  }

  protected isCallOrder(): boolean {
    return (
      this._fillOrderGroup.rowData.findIndex(
        (order) => order.orderType == CubiqTS.OrderType.LIMIT_CALL,
      ) > -1
    );
  }
}
